"use client";
/* eslint-disable @next/next/no-img-element */

import { useEffect, useState, useRef, useCallback, memo, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import {
  Search, MapPin, Star, MessageCircle, Phone, Heart,
  X, Mic, MicOff, ChevronRight,
  BadgeCheck, Award, Crown, Navigation, Filter, Camera, Building2, User,
  Volume2
} from 'lucide-react';
import { db } from '@/lib/firebase/client';
import { isSalon } from '@/lib/business';
import { useVoiceAssistantEnabled } from '@/hooks/useVoiceAssistantEnabled';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { CATEGORIES, getDistanceKm } from '@/lib/categories';
import { cityMatches, cityLabel } from '@/lib/cityMatch';
import { DynamicSEO } from '@/components/seo/DynamicSEO';
import dynamic from 'next/dynamic';

const VoiceAssistant = dynamic(() => import('@/components/ai/VoiceAssistant'), { ssr: false });

// ─── Types ───────────────────────────────────────────────────────────────────
interface Business {
  id: string;
  name: string;
  description?: string;
  category?: string;
  subCategory?: string;
  address?: string;
  city?: string;
  phone?: string;
  whatsapp?: string;
  rating?: number;
  reviewCount?: number;
  coverUrl?: string;
  avatarUrl?: string;
  services?: { name: string; price: number; duration: number }[];
  languages?: string[];
  parking?: boolean;
  lat?: number;
  lng?: number;
  verified?: boolean;
  topRated?: boolean;
  premium?: boolean;
  banned?: boolean;
  frozen?: boolean;
  businessType?: 'master' | 'salon';
  discountPercentage?: number;
  discount?: {
    percentage: number;
    validUntil: number | null;
    categories?: string[];
  };
}

interface UserLocation { lat: number; lng: number; }

// ─── Image Optimizer for Mock Data ────────────────────────────────────────────
function getOptimizedImageUrl(url: string | undefined | null, width: number, height: number): string | undefined {
  if (!url) return undefined;
  if (url.includes('images.unsplash.com')) {
    try {
      const u = new URL(url);
      u.searchParams.set('auto', 'format');
      u.searchParams.set('fit', 'crop');
      u.searchParams.set('q', '80');
      u.searchParams.set('w', width.toString());
      u.searchParams.set('h', height.toString());
      return u.toString();
    } catch {}
  }
  return url;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const CATEGORY_ICONS: Record<string, string> = {
  all: '🌟', hair: '✂️', nails: '💅', lashes: '👁️',
  makeup: '💄', massage: '🤲', cosmetology: '🌿',
  laser: '⚡', tattoo: '🖊️', piercing: '💎',
  apparatus: '🔬', injections: '💉', fitness: '🏋️',
  nutrition: '🥗', podology: '🦶', barber: '💈', tanning: '🌅',
};

const GRADIENT_FALLBACKS = [
  'from-pink-500 via-rose-500 to-red-400',
  'from-violet-500 via-purple-500 to-indigo-400',
  'from-amber-400 via-orange-500 to-red-400',
  'from-cyan-400 via-blue-500 to-indigo-500',
  'from-emerald-400 via-teal-500 to-cyan-500',
  'from-fuchsia-500 via-pink-500 to-rose-400',
];

const AVAILABLE_LANGUAGES = [
  { code: 'ru', label: 'Русский 🇷🇺' },
  { code: 'he', label: 'עברית 🇮🇱' },
  { code: 'en', label: 'English 🇬🇧' },
  { code: 'fr', label: 'Français 🇫🇷' },
  { code: 'ar', label: 'العربية 🇸🇦' },
];

const KEYWORD_MAP: { keywords: string[]; category: string; emoji: string }[] = [
  { keywords: ['маникюр', 'ногти', 'nail', 'manicure', 'nails', 'מניקור', 'ongles'], category: 'nails', emoji: '💅' },
  { keywords: ['стрижка', 'волосы', 'hair', 'haircut', 'coiffure', 'שיער'], category: 'hair', emoji: '✂️' },
  { keywords: ['ресницы', 'брови', 'lash', 'brow', 'ריסים', 'גבות'], category: 'lashes', emoji: '👁️' },
  { keywords: ['макияж', 'makeup', 'איפור', 'maquillage'], category: 'makeup', emoji: '💄' },
  { keywords: ['массаж', 'massage', 'עיסוי'], category: 'massage', emoji: '🤲' },
  { keywords: ['косметология', 'cosmetology', 'facial', 'קוסמטיקה'], category: 'cosmetology', emoji: '🌿' },
  { keywords: ['лазер', 'laser', 'эпиляция', 'epilat', 'הסרת שיער'], category: 'laser', emoji: '⚡' },
  { keywords: ['тату', 'tattoo', 'קעקוע'], category: 'tattoo', emoji: '🖊️' },
  { keywords: ['пирсинг', 'piercing', 'פירסינג'], category: 'piercing', emoji: '💎' },
  { keywords: ['аппаратная', 'apparatus', 'smas', 'lpg', 'rf', 'криотерапия', 'קוסמטיקה מכשירית', 'cryotherapy'], category: 'apparatus', emoji: '🔬' },
  { keywords: ['ботокс', 'инъекции', 'гиалуронов', 'botox', 'injection', 'hyaluronic', 'בוטוקס', 'חומצה היאלורונית', 'filler'], category: 'injections', emoji: '💉' },
  { keywords: ['фитнес', 'тренер', 'йога', 'fitness', 'trainer', 'yoga', 'כושר', 'יוגה', 'מאמן', 'pilates'], category: 'fitness', emoji: '🏋️' },
];

function getSuggestions(q: string, businesses: Business[]) {
  if (!q.trim()) return [];
  const lower = q.toLowerCase();
  const results: { type: 'category' | 'master'; label: string; sublabel?: string; emoji: string; value: string; category?: string }[] = [];
  KEYWORD_MAP.forEach(k => {
    if (k.keywords.some(w => w.includes(lower) || lower.includes(w.substring(0, 4)))) {
      results.push({ type: 'category', label: k.category, sublabel: k.emoji, emoji: k.emoji, value: k.category, category: k.category });
    }
  });
  businesses.filter(b => b.name?.toLowerCase().includes(lower) || b.description?.toLowerCase().includes(lower)).slice(0, 4)
    .forEach(b => results.push({ type: 'master', label: b.name, sublabel: b.city, emoji: CATEGORY_ICONS[b.category || ''] || '✨', value: b.name }));
  return results.slice(0, 8);
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function CatalogPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-background">
        {/* Sticky header skeleton */}
        <div className="sticky top-14 md:top-16 z-40 bg-background/95 border-b border-border/60">
          <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3">
            {/* Search bar skeleton */}
            <div className="flex gap-2 mb-3">
              <div className="flex-1 h-12 bg-card border-2 border-border rounded-2xl animate-pulse" />
              <div className="w-12 h-12 bg-card border-2 border-border rounded-2xl animate-pulse" />
            </div>
            {/* Category pills skeleton */}
            <div className="flex gap-2 overflow-hidden pb-2">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="h-10 w-24 bg-card border-2 border-border rounded-full animate-pulse shrink-0" />
              ))}
            </div>
          </div>
        </div>
        {/* Cards skeleton */}
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="h-5 w-40 bg-card rounded animate-pulse mb-3" />
          <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:space-y-0">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-card rounded-3xl animate-pulse border border-border" />
            ))}
          </div>
        </div>
      </div>
    }>
      <CatalogContent />
    </Suspense>
  );
}

/* K-0058: метка сборки на экране. Если на скрине из приложения метка старая —
   WebView крутит закэшированный билд, и это ответ. Обновляется каждым выкатом. */
const BUILD_STAMP = 'b2026-08-03-0405';

function CatalogContent() {
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const searchParams = useSearchParams();
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [filtered, setFiltered] = useState<Business[]>([]);
  /* K-0058: пустая витрина не имеет права молчать. Ошибка загрузки — видимая,
     с кнопкой повтора; «нет мастеров» показывается только когда база ЧЕСТНО
     ответила пустотой. */
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('service') || '');
  const [city, setCity] = useState(searchParams.get('city') || '');
  const [category, setCategory] = useState('all');
  const [subCategory, setSubCategory] = useState('all');
  const [sort, setSort] = useState<'rating' | 'name' | 'new' | 'distance'>('rating');
  const [liked, setLiked] = useState<Set<string>>(new Set());
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [suggestions, setSuggestions] = useState<ReturnType<typeof getSuggestions>>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [micSupported, setMicSupported] = useState(false);
  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [geoError, setGeoError] = useState(false);
  const [showVoiceTooltip, setShowVoiceTooltip] = useState(true);
  const [showVoiceAssistant, setShowVoiceAssistant] = useState(false);
  const { enabled: voiceEnabled } = useVoiceAssistantEnabled();

  // Advanced filters
  const [filterLanguage, setFilterLanguage] = useState('all');
  const [filterParking, setFilterParking] = useState<'any' | 'yes'>('any');
  const [filterRating, setFilterRating] = useState(0);
  const [filterDistance, setFilterDistance] = useState(50);
  const [businessTypeFilter, setBusinessTypeFilter] = useState<'all' | 'master' | 'salon'>('all');

  const searchRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const recogRef = useRef<unknown>(null);

  const allCategories = ['all', ...CATEGORIES.map(c => c.key)];
  const currentCatDef = CATEGORIES.find(c => c.key === category);

  // Apply category from URL service param (match keyword to category)
  useEffect(() => {
    const serviceParam = searchParams.get('service');
    if (serviceParam) {
      const lower = serviceParam.toLowerCase();
      const matched = KEYWORD_MAP.find(k => k.keywords.some(w => w.includes(lower) || lower.includes(w.substring(0, 4))));
      if (matched) {
        setCategory(matched.category);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    setMicSupported('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);
  }, []);

  // Auto-request geolocation on first visit
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
          setGeoError(false);
          // Auto-switch to distance sort if user hasn't manually changed
          setSort(prev => prev === 'rating' ? 'distance' : prev);
        },
        () => setGeoError(true),
        { timeout: 5000 }
      );
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
          searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Load businesses
  useEffect(() => {
    const fetchData = async () => {
      try {
        /* K-0054: каталог — самый посещаемый экран, и клиент ходит на него
           туда-обратно между профилями. Кэш на 5 минут в sessionStorage:
           повторные открытия в рамках сессии не стоят ни одного чтения. */
        const CACHE_KEY = 'yofi_catalog_v1';
        try {
          const raw = sessionStorage.getItem(CACHE_KEY);
          if (raw) {
            const cached = JSON.parse(raw);
            if (cached && Date.now() - cached.at < 5 * 60 * 1000 && Array.isArray(cached.data)) {
              setBusinesses(cached.data);
              setFiltered(cached.data);
              setLoading(false);
              return;
            }
          }
        } catch { /* повреждённый кэш игнорируем */ }

        const snap = await getDocs(collection(db, 'businesses'));
        const data = snap.docs.map(d => {
          const docData = d.data();
          return { 
            id: d.id, 
            ...docData,
            // Provide fallbacks for essential fields so they aren't accidentally filtered out
            // Both field names live in the data — normalize here so every
            // filter and link below can just read `businessType`.
            businessType: isSalon(docData) ? 'salon' : 'master',
            category: docData.category || 'other',
            name: docData.name || '',
          };
        }) as Business[];

        // For businesses without inline services, fetch from subcollection
        const enriched = await Promise.all(data.map(async (b) => {
          if (!b.services || b.services.length === 0) {
            try {
              const svcSnap = await getDocs(collection(db, 'businesses', b.id, 'services'));
              if (!svcSnap.empty) {
                const subServices = svcSnap.docs.map(d => {
                  const s = d.data();
                  return { name: s.name || '', price: s.price || 0, duration: s.duration || 0 };
                });
                return { ...b, services: subServices };
              }
            } catch { /* subcollection might not exist */ }
          }
          return b;
        }));

        // Hide banned/frozen masters from the public showcase (admins manage them in /admin).
        const visible = enriched.filter(b => !b.banned && !b.frozen);
        setBusinesses(visible);
        setFiltered(visible);
        // K-0058: пустоту не кэшируем — один сетевой сбой не должен давать
        // «нет мастеров» на 5 минут всем повторным заходам.
        if (visible.length > 0) {
          try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), data: visible })); } catch { /* квота — не страшно */ }
        } else {
          try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
        }
        setLoadError('');
      } catch (e) {
        console.error('[catalog] load failed:', e);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        setLoadError(String((e as any)?.code || (e as any)?.message || e));
        try { sessionStorage.removeItem('yofi_catalog_v1'); } catch { /* ignore */ }
      }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  // Load user's favorites from Firestore
  useEffect(() => {
    if (!user) return;
    const loadFavorites = async () => {
      try {
        const favsSnap = await getDocs(collection(db, 'users', user.uid, 'favorites'));
        const favIds = new Set(favsSnap.docs.map(d => d.id));
        setLiked(favIds);
      } catch (e) { console.error('Failed to load favorites:', e); }
    };
    loadFavorites();
  }, [user]);

  useEffect(() => {
    let result = [...businesses];
    if (showFavoritesOnly) result = result.filter(b => liked.has(b.id));
    if (businessTypeFilter !== 'all') result = result.filter(b => b.businessType === businessTypeFilter);
    if (category !== 'all') result = result.filter(b => b.category === category);
    if (subCategory !== 'all') result = result.filter(b => b.subCategory === subCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      const matched = KEYWORD_MAP.find(k => k.keywords.some(w => w.includes(q) || q.includes(w.substring(0, 4))));
      if (matched && category === 'all') {
        result = result.filter(b => b.category === matched.category);
      } else {
        result = result.filter(b =>
          b.name?.toLowerCase().includes(q) ||
          b.description?.toLowerCase().includes(q) ||
          b.services?.some(s => s.name.toLowerCase().includes(q))
        );
      }
    }
    if (city.trim()) {
      // Multilingual match: "Нетания" / "Netanya" / "נתניה" all match the same
      // master, so the assistant/search never wrongly hides everyone (cityMatch.ts).
      result = result.filter(b => cityMatches(b.city, city) || cityMatches(b.address, city));
    }
    if (filterLanguage !== 'all') result = result.filter(b => b.languages?.includes(filterLanguage));
    if (filterParking === 'yes') result = result.filter(b => b.parking === true);
    if (filterRating > 0) result = result.filter(b => (b.rating || 0) >= filterRating);
    if (userLocation && filterDistance < 50) {
      result = result.filter(b => {
        if (!b.lat || !b.lng) return true;
        return getDistanceKm(userLocation.lat, userLocation.lng, b.lat, b.lng) <= filterDistance;
      });
    }
    if (sort === 'rating') result.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    else if (sort === 'name') result.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    else if (sort === 'distance' && userLocation) {
      result.sort((a, b) => {
        const dA = a.lat && a.lng ? getDistanceKm(userLocation.lat, userLocation.lng, a.lat, a.lng) : 999;
        const dB = b.lat && b.lng ? getDistanceKm(userLocation.lat, userLocation.lng, b.lat, b.lng) : 999;
        return dA - dB;
      });
    }
    setFiltered(result);
  }, [businesses, category, subCategory, search, city, sort, filterLanguage, filterParking, filterRating, filterDistance, userLocation, businessTypeFilter, showFavoritesOnly, liked]);

  useEffect(() => {
    if (search.trim().length >= 2) {
      setSuggestions(getSuggestions(search, businesses));
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [search, businesses]);

  const detectLocation = useCallback(() => {
    navigator.geolocation?.getCurrentPosition(
      pos => { setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setGeoError(false); },
      () => setGeoError(true)
    );
  }, []);

  const toggleLike = useCallback((id: string) => {
    setLiked(prev => {
      const s = new Set(prev);
      if (s.has(id)) {
        s.delete(id);
        // Remove from Firestore
        if (user) deleteDoc(doc(db, 'users', user.uid, 'favorites', id)).catch(console.error);
      } else {
        s.add(id);
        // Save to Firestore
        if (user) setDoc(doc(db, 'users', user.uid, 'favorites', id), { addedAt: Date.now() }).catch(console.error);
      }
      return s;
    });
  }, [user]);

  const reset = () => {
    setSearch(''); setCity(''); setCategory('all'); setSubCategory('all');
    setSort('rating'); setFilterLanguage('all'); setFilterParking('any');
    setFilterRating(0); setFilterDistance(50); setBusinessTypeFilter('all');
    setShowFavoritesOnly(false);
  };

  const applySuggestion = (s: ReturnType<typeof getSuggestions>[0]) => {
    setSearch(s.type === 'category' ? '' : s.label);
    if (s.category) { setCategory(s.category); setSubCategory('all'); }
    setShowSuggestions(false);
    searchRef.current?.blur();
  };

  const startListening = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const recog: any = new SR();
    recog.lang = i18n.language === 'he' ? 'he-IL' : i18n.language === 'ru' ? 'ru-RU' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    recog.interimResults = false;
    recog.onstart = () => setIsListening(true);
    recog.onend = () => setIsListening(false);
    recog.onresult = (e: { results: { [x: number]: { [x: number]: { transcript: string } } } }) => {
      setSearch(e.results[0][0].transcript);
      searchRef.current?.focus();
    };
    recog.onerror = () => setIsListening(false);
    recogRef.current = recog;
    recog.start();
  };

  const stopListening = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (recogRef.current as any)?.stop();
    setIsListening(false);
  };

  const hasActiveFilters = search || city || category !== 'all' || filterLanguage !== 'all' || filterParking !== 'any' || filterRating > 0 || filterDistance < 50 || businessTypeFilter !== 'all' || showFavoritesOnly;

  return (
    <div className="min-h-screen bg-background">
      <DynamicSEO 
        pageType="catalog"
        city={city}
        category={category === 'all' ? '' : category}
      />

      {/* ── Sticky Header ── */}
      <div className="sticky top-14 md:top-16 z-40 bg-background/95 backdrop-blur-xl border-b border-border/60">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-3">

          {/* Smart Search row */}
          <div className="flex gap-2 mb-3">
            {/* Search input with autocomplete */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none z-10" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                onFocus={() => search.length >= 2 && setShowSuggestions(true)}
                placeholder={t('catalog.search_placeholder')}
                aria-label={t('catalog.search_placeholder')}
                className="w-full bg-card border-2 border-border rounded-2xl pl-11 pr-12 py-3.5 text-base font-medium outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/60"
                autoComplete="off"
              />
              {search && (
                <button onClick={() => { setSearch(''); setShowSuggestions(false); }}
                  aria-label="Clear search"
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}

              {/* Dropdown suggestions */}
              {showSuggestions && suggestions.length > 0 && (
                <div ref={dropdownRef}
                  className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-2xl shadow-2xl shadow-black/20 overflow-hidden z-50">
                  <div className="p-1">
                    {suggestions.map((s, i) => (
                      <button key={i} onClick={() => applySuggestion(s)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-primary/10 hover:text-primary transition-all text-left group">
                        <span className="text-xl shrink-0">{s.emoji}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold truncate">{s.type === 'category' ? t(`categories.${s.label}`) : s.label}</p>
                          {s.sublabel && <p className="text-xs text-muted-foreground truncate">{s.sublabel}</p>}
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold shrink-0 ${s.type === 'category' ? 'bg-primary/10 text-primary' : 'bg-secondary text-muted-foreground'}`}>
                          {s.type === 'category' ? '📂' : '👤'}
                        </span>
                        <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary shrink-0" />
                      </button>
                    ))}
                  </div>
                  {/* Quick category chips */}
                  <div className="border-t border-border px-3 py-2 bg-secondary/30">
                    <p className="text-xs text-muted-foreground font-bold uppercase tracking-wider mb-2">{t('home.categories_title')}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {KEYWORD_MAP.map(k => (
                        <button key={k.category}
                          onClick={() => { setCategory(k.category); setSubCategory('all'); setSearch(''); setShowSuggestions(false); }}
                          className="flex items-center gap-1 px-2.5 py-1 bg-card border border-border rounded-full text-xs font-bold hover:border-primary hover:bg-primary/10 hover:text-primary transition-all">
                          {k.emoji} {t('categories.' + k.category)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* City */}
            <div className="relative hidden sm:block">
              <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input type="text" value={city} onChange={e => setCity(e.target.value)}
                placeholder={t('catalog.city_placeholder')}
                aria-label={t('catalog.city_placeholder')}
                className="w-40 bg-card border-2 border-border rounded-2xl pl-11 pr-4 py-3.5 text-base font-medium outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all" />
            </div>

            {/* Geolocation */}
            <button onClick={detectLocation} title={t('dashboard_extra.location_btn')} aria-label={t('dashboard_extra.location_btn')}
              className={`flex items-center justify-center w-12 h-12 rounded-2xl border-2 transition-all shrink-0 ${userLocation ? 'bg-emerald-500 border-emerald-500 text-white' : geoError ? 'bg-red-500/10 border-red-500/30 text-red-500' : 'bg-card border-border text-muted-foreground hover:border-emerald-500/50 hover:text-emerald-500'}`}>
              <Navigation className="w-5 h-5" />
            </button>

            {/* Mic — K-0059: голосовой поиск скрыт тем же админ-флагом, что и
                ассистент (system_config/voiceAssistant.enabled). Распознавание
                на 4 языках пока путает очереди — Денис велел спрятать, код
                остаётся, включается одним переключателем в админке. */}
            {micSupported && voiceEnabled && (
              <button onClick={isListening ? stopListening : startListening}
                aria-label={isListening ? 'Stop voice search' : 'Start voice search'}
                className={`flex items-center justify-center w-12 h-12 rounded-2xl border-2 transition-all shrink-0 ${isListening ? 'bg-red-500 border-red-500 text-white animate-pulse shadow-lg shadow-red-500/30' : 'bg-card border-border text-muted-foreground hover:border-primary/50 hover:text-primary'}`}>
                {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>
            )}

            {/* Filters toggle */}
            <button onClick={() => setShowFilters(v => !v)}
              className={`flex items-center gap-2 px-4 py-3 rounded-2xl border-2 text-base font-bold transition-all ${showFilters ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border hover:border-primary/50'}`}>
              <Filter className="w-5 h-5" />
              <span className="hidden sm:inline">{t('catalog.filters')}</span>
            </button>

            {/* Reset */}
            {hasActiveFilters && (
              <button onClick={reset}
                aria-label={t('catalog.reset')}
                className="flex items-center gap-1 px-4 py-3 bg-red-500/10 text-red-500 rounded-2xl border-2 border-red-500/20 text-base font-bold hover:bg-red-500/20 transition-all">
                <X className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Listening indicator */}
          {isListening && (
            <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-xl">
              <div className="flex gap-0.5 items-center">
                {[1, 2, 3, 4].map(i => (
                  <div key={i} className="w-1 bg-red-500 rounded-full animate-bounce" style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
              <span className="text-sm text-red-600 font-bold">🎙️ {t('dashboard_extra.location_btn')}...</span>
            </div>
          )}

          {/* Advanced filters panel */}
          {showFilters && (
            <div className="mb-2 p-3 bg-card border border-border rounded-2xl grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Sort */}
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">{t('catalog.sort_label')}</label>
                <div className="flex flex-wrap gap-1">
                  {(['rating', 'name', 'distance'] as const).map(s => (
                    <button key={s} onClick={() => setSort(s)}
                      className={`px-2.5 py-1 rounded-full text-xs font-bold border transition-all ${sort === s ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:border-primary/50'}`}>
                      {s === 'rating' ? t('catalog.sort_rating') : s === 'name' ? t('catalog.sort_name') : '📍'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">{t('catalog.filter_language')}</label>
                <select value={filterLanguage} onChange={e => setFilterLanguage(e.target.value)}
                  className="w-full bg-background border border-border rounded-lg px-2 py-1.5 text-xs font-bold outline-none focus:border-primary transition-colors">
                  {[{code: 'all', label: t('catalog.all_langs')}, ...AVAILABLE_LANGUAGES].map(l => (
                    <option key={l.code} value={l.code}>{l.label}</option>
                  ))}
                </select>
              </div>

              {/* Parking */}
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">{t('catalog.filter_parking')}</label>
                <div className="flex gap-1">
                  {(['any', 'yes'] as const).map(p => (
                    <button key={p} onClick={() => setFilterParking(p)}
                      className={`flex-1 px-2 py-1 rounded-full text-xs font-bold border transition-all ${filterParking === p ? 'bg-primary text-primary-foreground border-primary' : 'bg-background border-border hover:border-primary/50'}`}>
                      {p === 'any' ? t('catalog.parking_any') : '🅿️'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Min rating */}
              <div>
                <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                  {t('catalog.filter_rating')}: {filterRating > 0 ? `${filterRating}+⭐` : t('catalog.any_rating')}
                </label>
                <input type="range" min={0} max={5} step={0.5} value={filterRating} onChange={e => setFilterRating(Number(e.target.value))}
                  aria-label={t('catalog.filter_rating')}
                  className="w-full accent-primary" />
              </div>

              {/* Distance */}
              {userLocation && (
                <div className="col-span-2 sm:col-span-4">
                  <label className="text-xs text-muted-foreground font-bold uppercase tracking-wider block mb-1.5">
                    {t('catalog.filter_distance')}: {filterDistance < 50 ? `≤ ${filterDistance} km` : t('catalog.any_rating')}
                  </label>
                  <input type="range" min={1} max={50} value={filterDistance} onChange={e => setFilterDistance(Number(e.target.value))}
                    aria-label={t('catalog.filter_distance')}
                    className="w-full accent-primary" />
                </div>
              )}
            </div>
          )}

          {/* Master / Salon toggle */}
          <div className="flex gap-2 mb-3">
            {([{ key: 'all', label: 'catalog.all_types', icon: null, emoji: '🌟' }, { key: 'master', label: 'catalog.type_master', icon: User, emoji: '👤' }, { key: 'salon', label: 'catalog.type_salon', icon: Building2, emoji: '🏪' }] as const).map(item => (
              <button
                key={item.key}
                onClick={() => { setBusinessTypeFilter(item.key as 'all' | 'master' | 'salon'); setShowFavoritesOnly(false); }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-black whitespace-nowrap border-2 transition-all shrink-0 ${
                  !showFavoritesOnly && businessTypeFilter === item.key
                    ? item.key === 'salon'
                      ? 'bg-linear-to-r from-violet-500 to-purple-600 text-white border-violet-500 shadow-lg shadow-violet-500/30'
                      : item.key === 'master'
                        ? 'bg-linear-to-r from-pink-500 to-rose-500 text-white border-pink-500 shadow-lg shadow-pink-500/30'
                        : 'bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20'
                    : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'
                }`}
              >
                <span className="text-base">{item.emoji}</span>
                {t(item.label)}
              </button>
            ))}

            {/* Favorites filter — only for logged-in users with saved masters */}
            {user && liked.size > 0 && (
              <button
                onClick={() => setShowFavoritesOnly(v => !v)}
                aria-label={t('catalog.favorites', 'Избранное')}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-2xl text-sm font-black whitespace-nowrap border-2 transition-all shrink-0 ${
                  showFavoritesOnly
                    ? 'bg-red-500 text-white border-red-500 shadow-lg shadow-red-500/30'
                    : 'bg-card border-border text-red-500 hover:border-red-300'
                }`}
              >
                <Heart className={`w-4 h-4 ${showFavoritesOnly ? 'fill-current' : ''}`} />
                {t('catalog.favorites', 'Избранное')}
              </button>
            )}
          </div>

          {/* L1 Category pills */}
          <div className="relative">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none scroll-fade-x">
              {allCategories.map(cat => (
                <button key={cat} onClick={() => { setCategory(cat); setSubCategory('all'); }}
                  className={`flex items-center gap-1.5 px-4 py-2.5 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${category === cat ? 'bg-primary text-primary-foreground border-primary shadow-md shadow-primary/20' : 'bg-card border-border text-muted-foreground hover:border-primary/40 hover:text-foreground'}`}>
                  <span className="text-base">{CATEGORY_ICONS[cat]}</span>
                  {t(`categories.${cat}`)}
                </button>
              ))}
            </div>
          </div>

          {/* L2 Sub-category pills */}
          {currentCatDef && (
            <div className="relative">
              <div className="flex gap-2 overflow-x-auto pb-2 mt-2 scrollbar-none scroll-fade-x">
                <button onClick={() => setSubCategory('all')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${subCategory === 'all' ? 'bg-primary/20 text-primary border-primary/40' : 'bg-secondary border-border text-muted-foreground hover:border-primary/30'}`}>
                  {t('catalog.sub_all')}
                </button>
                {currentCatDef.subs.map(sub => (
                  <button key={sub.key} onClick={() => setSubCategory(sub.key)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap border-2 transition-all shrink-0 ${subCategory === sub.key ? 'bg-primary/20 text-primary border-primary/40' : 'bg-secondary border-border text-muted-foreground hover:border-primary/30'}`}>
                    <span>{sub.emoji}</span>
                    {t(`categories.${sub.key}`)}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Results count - always occupies space to prevent CLS */}
        <p className="text-sm text-muted-foreground mb-3 font-medium px-1 h-5">
          {!loading && (
            <>{t('catalog.found')} <span className="text-foreground font-black">{filtered.length}</span> {t('catalog.masters')}</>
          )}
        </p>

        {/* Loading skeletons */}
        {loading && (
          <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-4 sm:space-y-0">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] bg-card rounded-3xl animate-pulse border border-border" />
            ))}
          </div>
        )}

        {/* K-0058: ошибка загрузки — НЕ «нет мастеров». Пустая витрина из-за
            сети/кэша молчала, и это выглядело как пропажа всей базы. */}
        {!loading && loadError && businesses.length === 0 && (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">📡</div>
            <h3 className="text-xl font-black mb-2">{t('catalog.load_error_title', 'החיבור נכשל — המומחים במקום')}</h3>
            <p className="text-muted-foreground mb-2">{t('catalog.load_error_text', 'לא הצלחנו לטעון את הקטלוג. בדקו אינטרנט ונסו שוב.')}</p>
            <p className="text-xs text-muted-foreground mb-6 font-mono" dir="ltr">{loadError}</p>
            <button onClick={() => { try { sessionStorage.removeItem('yofi_catalog_v1'); } catch {} window.location.reload(); }}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-bold hover:bg-primary/90 transition-all">
              {t('catalog.retry', 'לנסות שוב')}
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !loadError && filtered.length === 0 && (
          <div className="text-center py-24">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-black mb-2">{t('catalog.empty_title')}</h3>
            <p className="text-muted-foreground mb-6">{t('catalog.empty_text')}</p>
            {/* K-0058: самодиагностика на экране — по скрину видно, ЧТО спрятало
                мастеров: фильтры (и какие) или пустая база. */}
            <p className="text-xs text-muted-foreground mb-4 font-mono" dir="ltr">
              {businesses.length > 0
                ? `loaded=${businesses.length} · filters: ${[
                    search && `q=${search}`, city && `city=${city}`,
                    category !== 'all' && `cat=${category}`,
                    businessTypeFilter !== 'all' && `type=${businessTypeFilter}`,
                    filterLanguage !== 'all' && `lang=${filterLanguage}`,
                    showFavoritesOnly && 'favorites',
                    filterRating > 0 && `rating>${filterRating}`,
                  ].filter(Boolean).join(', ') || 'none'}`
                : `loaded=0 (база ответила пусто)`} · {BUILD_STAMP}
            </p>
            <button onClick={reset} className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-bold hover:bg-primary/90 transition-all">
              {t('catalog.reset')}
            </button>
          </div>
        )}

        {/* ─── Instagram-style grid ─── */}
        {!loading && filtered.length > 0 && (
          <div className="space-y-4 sm:grid sm:grid-cols-2 lg:grid-cols-4 sm:gap-4 sm:space-y-0">
            {filtered.map((b, idx) => (
              <MasterCard
                key={b.id}
                business={b}
                liked={liked.has(b.id)}
                onLike={() => toggleLike(b.id)}
                gradientFallback={GRADIENT_FALLBACKS[idx % GRADIENT_FALLBACKS.length]}
                userLocation={userLocation}
                t={t}
                lang={i18n.language}
                index={idx}
              />
            ))}
          </div>
        )}
      </div>

      {/* Voice Assistant — hidden unless an admin enables it (system_config/voiceAssistant) */}
      {voiceEnabled && (
      <>
      {/* ── Floating Voice Assistant Button ── */}
      <div className="fixed bottom-20 right-4 z-50 flex flex-col items-end gap-2">
        {/* Tooltip bubble */}
        {showVoiceTooltip && (
          <div className="relative animate-bounce-slow">
            <div className="bg-linear-to-r from-violet-500 to-purple-600 text-white px-4 py-3 rounded-2xl shadow-2xl shadow-purple-500/30 max-w-[200px] text-sm font-bold leading-snug">
              {t('catalog.voice_assistant_hint')}
              <button
                onClick={(e) => { e.stopPropagation(); setShowVoiceTooltip(false); }}
                aria-label="Close tooltip"
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-white/80 hover:text-white text-xs"
              >
                ✕
              </button>
            </div>
            {/* Triangle pointer */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 bg-purple-600 rotate-45 rounded-sm" />
          </div>
        )}
        {/* Voice AI Assistant button — opens full AI chat, NOT just SpeechRecognition */}
        <button
          onClick={() => { setShowVoiceTooltip(false); setShowVoiceAssistant(true); }}
          aria-label={t('voice.fab_label')}
          className="w-16 h-16 bg-linear-to-br from-violet-500 via-purple-500 to-fuchsia-500 text-white rounded-full flex items-center justify-center shadow-2xl shadow-purple-500/40 hover:shadow-purple-500/60 hover:scale-110 active:scale-95 transition-all"
        >
          <Volume2 className="w-7 h-7" />
        </button>
      </div>

      {/* ── Voice Assistant Modal (Full AI Chat) ── */}
      <VoiceAssistant
        isOpen={showVoiceAssistant}
        onClose={() => setShowVoiceAssistant(false)}
      />
      </>
      )}
    </div>
  );
}

// ─── Instagram-Style Master Card (memoized to prevent unnecessary re-renders) ──
const MasterCard = memo(function MasterCard({
  business: b, liked, onLike, gradientFallback, userLocation, t, lang, index,
}: {
  business: Business;
  liked: boolean;
  onLike: () => void;
  gradientFallback: string;
  userLocation: UserLocation | null;
  t: (k: string) => string;
  /** K-0069: язык интерфейса — город показываем на нём, а не на иврите. */
  lang?: string;
  index?: number;
}) {
  const [now] = useState(() => Date.now());

  const isDiscountActive = () => {
    if (b.discount) {
      if (b.discount.percentage <= 0) return false;
      if (!b.discount.validUntil) return true;
      return b.discount.validUntil > now;
    }
    return (b.discountPercentage || 0) > 0;
  };

  const getDiscountedPrice = (price: number) => {
    if (!isDiscountActive()) return price;
    const percentage = b.discount?.percentage || b.discountPercentage || 0;
    return Math.round(price * (1 - percentage / 100));
  };

  const minPrice = b.services?.length ? Math.min(...b.services.map(s => getDiscountedPrice(s.price))) : null;
  const maxPrice = b.services?.length ? Math.max(...b.services.map(s => getDiscountedPrice(s.price))) : null;
  const hasValidCoords = typeof b.lat === 'number' && typeof b.lng === 'number' && b.lat !== 0 && b.lng !== 0 && !isNaN(b.lat) && !isNaN(b.lng);
  const distance = (userLocation && hasValidCoords)
    ? getDistanceKm(userLocation.lat, userLocation.lng, b.lat!, b.lng!)
    : null;
  const topServices = b.services?.slice(0, 3) || [];

  return (
    <div className={`card-snap${index !== undefined && index > 5 ? ' cv-auto' : ''}`}>
      <Link href={b.businessType === 'salon' ? `/salon?id=${b.id}` : `/master?id=${b.id}`} className="block">
        <div className="relative rounded-3xl overflow-hidden border border-border/60 bg-card shadow-sm hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 group">

          {/* ── Photo Section (70% on mobile) ── */}
          <div className={`relative aspect-[4/5] sm:aspect-square lg:aspect-[5/4] bg-linear-to-br ${gradientFallback} overflow-hidden`}>
            {b.coverUrl && (
              <img
                src={getOptimizedImageUrl(b.coverUrl, 400, 500)}
                alt={b.name}
                width={400}
                height={500}
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                decoding={index !== undefined && index < 2 ? 'sync' : 'async'}
                className={`absolute inset-0 w-full h-full object-cover ${index !== undefined && index < 2 ? '' : 'group-hover:scale-105 transition-transform duration-500'}`}
                loading={index !== undefined && index < 4 ? 'eager' : 'lazy'}
                {...(index !== undefined && index < 2 ? { fetchPriority: 'high' as const } : {})}
              />
            )}

            {/* Gradient overlays */}
            <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute inset-0 bg-linear-to-b from-black/30 via-transparent to-transparent h-24" />

            {/* ── Top: Badges ── */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
              {/* Business type badge */}
              {b.businessType === 'salon' && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-linear-to-r from-violet-500 to-purple-600 text-white text-[11px] font-black rounded-full shadow-lg">
                  <Building2 className="w-3 h-3" /> {t('catalog.type_salon')}
                </div>
              )}
              {b.premium && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-linear-to-r from-amber-400 to-amber-600 text-white text-[11px] font-black rounded-full shadow-lg badge-shimmer">
                  <Crown className="w-3 h-3" /> VIP
                </div>
              )}
              {b.verified && !b.premium && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/90 backdrop-blur-sm text-white text-[11px] font-black rounded-full shadow-lg">
                  <BadgeCheck className="w-3 h-3" /> ✓
                </div>
              )}
              {b.topRated && (
                <div className="flex items-center gap-1 px-2.5 py-1 bg-violet-500/90 backdrop-blur-sm text-white text-[11px] font-black rounded-full shadow-lg">
                  <Award className="w-3 h-3" /> TOP
                </div>
              )}
            </div>

            {/* ── Top Right: Like ── */}
            <button onClick={e => { e.preventDefault(); onLike(); }}
              aria-label={liked ? 'Remove from favorites' : 'Add to favorites'}
              className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all shadow-lg z-10 backdrop-blur-md ${liked ? 'bg-red-500 text-white scale-110' : 'bg-white/20 text-white hover:bg-red-500 hover:scale-110'}`}>
              <Heart className={`w-5 h-5 ${liked ? 'fill-current' : ''}`} />
            </button>

            {/* ── Bottom of Photo: Master Identity ── */}
            <div className="absolute bottom-0 left-0 right-0 p-4 z-10">
              <div className="flex items-end justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full border-2 border-white/50 bg-white/20 backdrop-blur-md flex items-center justify-center text-white font-black text-lg shrink-0 overflow-hidden shadow-lg">
                    {b.avatarUrl ? <img src={getOptimizedImageUrl(b.avatarUrl, 96, 96)} alt="" width={48} height={48} decoding="async" loading="lazy" className="w-full h-full object-cover" /> : b.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    {/* Two lines, not one: "Oxy Hair Studio Netanya" lost a
                        third of itself to truncate, and the business name is
                        what the client is choosing between. */}
                    <p className="text-white font-black text-lg leading-tight line-clamp-2 drop-shadow-lg">{b.name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {b.city && (
                        <span className="text-white/80 text-xs font-medium flex items-center gap-1 drop-shadow">
                          <MapPin className="w-3 h-3" />{cityLabel(b.city, lang)}
                        </span>
                      )}
                      {distance !== null && (
                        <span className="text-emerald-300 text-xs font-bold drop-shadow">
                          {distance.toFixed(1)} km
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Rating pill */}
                <div className="flex items-center gap-1 bg-black/40 backdrop-blur-md px-2.5 py-1.5 rounded-full shrink-0">
                  <Star className={`w-3.5 h-3.5 ${b.reviewCount && b.reviewCount > 0 ? 'text-amber-400 fill-amber-400' : 'text-muted-foreground'}`} />
                  <span className="text-white text-sm font-black">{b.reviewCount && b.reviewCount > 0 ? (b.rating?.toFixed(1) || '—') : '—'}</span>
                  {b.reviewCount ? <span className="text-white/60 text-[10px]">({b.reviewCount})</span> : null}
                </div>
              </div>
            </div>

            {/* ── Parking badge ── */}
            {b.parking && (
              <div className="absolute bottom-16 left-4 px-2 py-1 bg-blue-500/80 backdrop-blur-sm text-white text-[10px] font-bold rounded-full z-10">
                🅿️ {t('catalog.filter_parking')}
              </div>
            )}
          </div>

          {/* ── Info Section ── */}
          <div className="p-4">
            {/* Services preview */}
            {topServices.length > 0 && (
              <div className="space-y-1.5 mb-3">
                {topServices.map((s, i) => {
                  const discountedPrice = getDiscountedPrice(s.price);
                  const hasDiscount = discountedPrice < s.price;
                  return (
                  <div key={i} className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground truncate">{s.name}</span>
                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                       {hasDiscount && <span className="text-[10px] line-through text-muted-foreground font-bold">₪{s.price}</span>}
                       <span className={`text-sm font-black ${hasDiscount ? 'text-amber-500' : 'text-primary'}`}>₪{discountedPrice}</span>
                    </div>
                  </div>
                )})}
              </div>
            )}

            {/* Price range + Languages + Actions */}
            <div className="flex items-center justify-between pt-2 border-t border-border/50">
              <div className="flex items-center gap-2 min-w-0">
                {minPrice && maxPrice && (
                  <span className="text-xs font-bold bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                    ₪{minPrice}–{maxPrice}
                  </span>
                )}
                {b.languages && b.languages.length > 0 && (
                  <span className="text-[10px] text-muted-foreground font-medium truncate">
                    {b.languages.map(l => l.toUpperCase()).join(' · ')}
                  </span>
                )}
              </div>

              {/* Quick action buttons */}
              <div className="flex items-center gap-1.5 shrink-0">
                {b.whatsapp && (
                  <div
                    onClick={e => { e.preventDefault(); window.open(`https://wa.me/${b.whatsapp?.replace(/\D/g, '')}`, '_blank'); }}
                    className="w-8 h-8 bg-green-500/10 hover:bg-green-500 text-green-600 hover:text-white rounded-full flex items-center justify-center transition-all active:scale-95 border border-green-500/20"
                  >
                    <MessageCircle className="w-4 h-4" />
                  </div>
                )}
                {b.phone && (
                  <div
                    onClick={e => { e.preventDefault(); window.open(`tel:${b.phone}`, '_self'); }}
                    className="w-8 h-8 bg-primary/10 hover:bg-primary text-primary hover:text-primary-foreground rounded-full flex items-center justify-center transition-all active:scale-95 border border-primary/20"
                  >
                    <Phone className="w-4 h-4" />
                  </div>
                )}
                <div className="w-8 h-8 bg-secondary hover:bg-primary/10 text-muted-foreground hover:text-primary rounded-full flex items-center justify-center transition-all active:scale-95 border border-border">
                  <Camera className="w-4 h-4" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
});

MasterCard.displayName = 'MasterCard';
